import React, { useState } from 'react';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import GenericModal from '~/components/GenericModal/GenericModal';
import Spacer from '~/components/Spacer/Spacer';
import Spinner from '~/components/Spinner/Spinner';
import ToggleSwitch from '~/components/ToggleSwitch/ToggleSwitch';
import usePartnerBuildingApproval, {
  PAGE_SIZE_OPTIONS
} from '~/hooks/partnerBuildingApproval/usePartnerBuildingApproval';
import { PendingPartnerBuilding } from '~/types/partnerBuildingApproval';
import classes from './PartnerBuildingApproval.module.css';

const formatDate = (value: string): string => new Date(value).toLocaleString();

const statusClass = (status: PendingPartnerBuilding['status']): string => {
  if (status === 'rejected') {
    return classes.statusRejected;
  }
  return status === 'approving'
    ? classes.statusApproving
    : classes.statusPending;
};

const PartnerBuildingApproval: React.FC = () => {
  const {
    buildings,
    actionableBuildings,
    loadingConfig,
    showRejected,
    setShowRejected,
    selectedUuids,
    toggleSelection,
    toggleSelectAll,
    approveSelected,
    rejectBuilding,
    isSubmitting,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    approvalStatus
  } = usePartnerBuildingApproval();

  const [payloadToView, setPayloadToView] =
    useState<PendingPartnerBuilding | null>(null);
  const [buildingToReject, setBuildingToReject] =
    useState<PendingPartnerBuilding | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const closeRejectModal = () => {
    setBuildingToReject(null);
    setRejectionReason('');
  };

  const confirmReject = () => {
    if (!buildingToReject) {
      return;
    }
    rejectBuilding(buildingToReject.uuid, rejectionReason.trim() || undefined);
    closeRejectModal();
  };

  const progress = approvalStatus;
  const processed = progress ? progress.completed + progress.failed : 0;
  const percent =
    progress && progress.total > 0
      ? Math.round((processed / progress.total) * 100)
      : 0;

  const renderProgress = () =>
    progress && (
      <div className={classes.progressBanner}>
        <div className={classes.progressHeader}>
          <span className={classes.progressLabel}>
            Approving {processed} of {progress.total} buildings…
          </span>
          <span className={classes.progressPercent}>{percent}%</span>
        </div>
        <div className={classes.progressTrack}>
          <div
            className={classes.progressFill}
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className={classes.progressHint}>
          {progress.failed > 0 && `${progress.failed} failed. `}
          This runs in the background — you can leave this page.
        </span>
      </div>
    );

  // The batch keeps running while the list refetches, so the progress banner renders above
  // the spinner rather than being replaced by it.
  if (loadingConfig.flag) {
    return (
      <div className='content-container'>
        {renderProgress()}
        <div className={classes.loadingContainer}>
          <Spinner />
          <h3 style={{ textAlign: 'center' }}>{loadingConfig.comment}</h3>
        </div>
      </div>
    );
  }

  // Compares membership rather than sizes so the box can't tick from a coincidental count
  // match when the listed rows have changed underneath the selection.
  const allSelected =
    actionableBuildings.length > 0 &&
    actionableBuildings.every((b) => selectedUuids.has(b.uuid));

  const renderPager = (showPageSize: boolean) => (
    <div className={classes.pagination}>
      <span className={classes.paginationCount}>
        Page {page} of {totalPages} ({totalCount} total)
      </span>
      {showPageSize && (
        <select
          className={classes.pageSizeSelect}
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          disabled={isSubmitting}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      )}
      <button
        className='btn-standard btn-secondary'
        onClick={() => setPage(page - 1)}
        disabled={page <= 1 || isSubmitting}
      >
        Previous
      </button>
      <button
        className='btn-standard btn-secondary'
        onClick={() => setPage(page + 1)}
        disabled={page >= totalPages || isSubmitting}
      >
        Next
      </button>
    </div>
  );

  return (
    <div className='content-container'>
      <Breadcrumbs />
      <Spacer height={20} />

      <div className={classes.header}>
        <h1>Partner Building Approval</h1>
        <div className={classes.headerActions}>
          <div className={classes.toggleGroup}>
            <span className={classes.toggleLabel}>Show Rejected Buildings</span>
            <ToggleSwitch
              checked={showRejected}
              onChange={setShowRejected}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      <Spacer height={20} />

      {renderProgress()}

      {totalCount > 0 && renderPager(true)}

      {buildings.length === 0 ? (
        <div className={classes.noDataContainer}>
          <p className='default-text'>No buildings are awaiting approval</p>
        </div>
      ) : (
        <div className={classes.tableWrapper}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.checkboxCell}>
                  <input
                    type='checkbox'
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    disabled={actionableBuildings.length === 0 || isSubmitting}
                  />
                </th>
                <th>Building Name</th>
                <th>CID</th>
                <th>Property ID</th>
                <th>Address</th>
                <th>Plan</th>
                <th>Received At</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {buildings.map((building) => {
                const isRejected = building.status === 'rejected';
                // A stale claim is actionable again: its worker died and nothing else will ever
                // release it. A live 'approving' row stays locked.
                const isActionable =
                  building.status === 'pending' || building.isStaleClaim;

                return (
                  <tr
                    key={building.uuid}
                    className={`${classes.clickableRow} ${
                      isRejected ? classes.rejectedRow : ''
                    }`}
                    onClick={() => setPayloadToView(building)}
                  >
                    <td
                      className={classes.checkboxCell}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isActionable && (
                        <input
                          type='checkbox'
                          checked={selectedUuids.has(building.uuid)}
                          onChange={() => toggleSelection(building.uuid)}
                          disabled={isSubmitting}
                        />
                      )}
                    </td>
                    <td>{building.buildingName}</td>
                    <td className={classes.monospace}>
                      {building.clientId || '—'}
                    </td>
                    <td className={classes.monospace}>{building.propertyId}</td>
                    <td>{building.address || '—'}</td>
                    <td>{building.isPaid ? 'Paid' : 'Free'}</td>
                    <td>{formatDate(building.dateSent)}</td>
                    <td>
                      <span
                        className={`${classes.statusBadge} ${statusClass(
                          building.status
                        )}`}
                      >
                        {building.isStaleClaim ? 'interrupted' : building.status}
                      </span>
                      {building.isStaleClaim && (
                        <div className={classes.reasonText}>
                          Approval stopped partway — approve again to retry.
                        </div>
                      )}
                      {isRejected && building.rejectionReason && (
                        <div className={classes.reasonText}>
                          {building.rejectionReason}
                        </div>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {isActionable && (
                        <button
                          className={classes.rejectBtn}
                          onClick={() => setBuildingToReject(building)}
                          disabled={isSubmitting}
                        >
                          Reject
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalCount > 0 && renderPager(false)}

      {selectedUuids.size > 0 && (
        <div className={classes.footer}>
          <span className={classes.footerCount}>
            {selectedUuids.size} of {actionableBuildings.length} selected
          </span>
          <button
            className='btn-primary btn-standard bold'
            onClick={approveSelected}
            disabled={isSubmitting || progress !== null}
          >
            {isSubmitting || progress
              ? 'Working...'
              : `Approve all (${selectedUuids.size})`}
          </button>
        </div>
      )}

      <GenericModal
        showModal={payloadToView !== null}
        title={payloadToView ? `Payload — ${payloadToView.buildingName}` : ''}
        hideActionButton
        onChange={() => setPayloadToView(null)}
        content={
          <pre className={classes.payloadPre}>
            {payloadToView
              ? JSON.stringify(payloadToView.payload, null, 2)
              : ''}
          </pre>
        }
      />

      <GenericModal
        showModal={buildingToReject !== null}
        title={
          buildingToReject ? `Reject ${buildingToReject.buildingName}` : ''
        }
        actionText='Reject'
        actionFunction={confirmReject}
        onChange={(open: boolean) => {
          if (!open) {
            closeRejectModal();
          }
        }}
        content={
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 480 }}>
            <label className='small-text bold'>Reason (optional)</label>
            <textarea
              className='default-text'
              rows={4}
              value={rejectionReason}
              placeholder='Why is this building being rejected? (optional)'
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
        }
      />
    </div>
  );
};

export default PartnerBuildingApproval;
